// src/axiosInstance.js
import axios from 'axios';
import { isAbortError } from './requestUtils';
import { API_BASE } from './apiConfig';
import {
  getRefreshPromise,
  isAuthEndpointUrl,
  redirectToLoginExpired,
  refreshSession,
} from '../auth/sessionRefresh';

const axiosInstance = axios.create({
  baseURL: API_BASE,
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,
});

export default axiosInstance;
export { API_BASE };
export { REPORTING_HOST } from './apiConfig';

function isPublicApiUrl(url = '') {
  const u = (url || '').toLowerCase();
  return u.includes('tenant-settings/login-config');
}

function isLoginAttemptEndpoint(url = '') {
  const u = (url || '').toLowerCase();
  return (
    u.includes('/webusers/login') ||
    u.includes('webusers/login') ||
    u.includes('/webusers/windows-login') ||
    u.includes('webusers/windows-login') ||
    u.includes('/webusers/windows-password-login') ||
    u.includes('webusers/windows-password-login')
  );
}

function normalizeDateString(value) {
  if (typeof value !== 'string') return value;

  let m = value.match(/^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2}:\d{2})(\.\d+)?$/);
  if (m) {
    return `${m[1]} ${m[2]}`;
  }

  m = value.match(/^(\d{2}:\d{2}:\d{2})(\.\d+)?$/);
  if (m) {
    return m[1];
  }

  return value;
}

function normalizeDatesDeep(data) {
  if (data == null) return data;

  if (Array.isArray(data)) {
    return data.map(normalizeDatesDeep);
  }

  if (typeof data === 'object') {
    const next = {};
    for (const [k, v] of Object.entries(data)) {
      if (typeof v === 'string') {
        next[k] = normalizeDateString(v);
      } else {
        next[k] = normalizeDatesDeep(v);
      }
    }
    return next;
  }

  if (typeof data === 'string') {
    return normalizeDateString(data);
  }

  return data;
}

axiosInstance.interceptors.request.use(
  async (config) => {
    if (config.signal?.aborted) {
      return Promise.reject(new axios.CanceledError('Request aborted', config));
    }
    if (isAuthEndpointUrl(config.url)) return config;

    const inFlight = getRefreshPromise();
    if (inFlight) await inFlight;

    if (config.signal?.aborted) {
      return Promise.reject(new axios.CanceledError('Request aborted', config));
    }
    return config;
  },
  (err) => Promise.reject(err)
);

axiosInstance.interceptors.response.use(
  (res) => {
    if (res && typeof res.data !== 'undefined') {
      res.data = normalizeDatesDeep(res.data);
    }
    return res;
  },
  async (err) => {
    if (isAbortError(err)) {
      return Promise.reject(err);
    }

    const status = err?.response?.status;
    const original = err?.config;

    if (status !== 401 || !original) {
      return Promise.reject(err);
    }

    if (isLoginAttemptEndpoint(original.url) || isPublicApiUrl(original.url)) {
      return Promise.reject(err);
    }

    if (isAuthEndpointUrl(original.url)) {
      redirectToLoginExpired();
      return Promise.reject(err);
    }

    if (original._retry) {
      redirectToLoginExpired();
      return Promise.reject(err);
    }
    original._retry = true;

    try {
      await refreshSession();
      return axiosInstance(original);
    } catch (refreshErr) {
      redirectToLoginExpired();
      return Promise.reject(refreshErr);
    }
  }
);
